/**
 * A verificação do assistente técnico item a item na revisão facial (E172.F4, GUS-230 B2.3).
 *
 * O protocolo FISWG adotado termina na verificação: a qualidade de cada imagem e cada componente
 * do parecer, confirmados ou retificados. Cada item mostra o que a leitura registrou; retificar
 * pede o que o assistente técnico observa no lugar. A lista é a do parecer em vigor
 * (`itensDaVerificacao`, a mesma régua do servidor); a tela não inventa item.
 *
 * E180.F2 (GUS-254 item 4, ADR-0133): a verificação é facultativa — vai ao servidor só o item
 * decidido; o que não foi decidido segue o que o sistema apurou. Só o retificado sem o que se
 * observa no lugar segura o envio.
 */
import { useState } from 'react';

import { Field, Radio, Textarea } from '@/components/ui';
import { ptBR } from '@/i18n/pt-BR';
import {
  itensDaVerificacao,
  type ConferenciaFacialParecerData,
  type ElementoDoPainel,
  type QualidadeDaImagemData,
} from '@/lib/conferencia_facial';
import type { DecisaoDoItem, VerificacaoDoItemData } from '@/lib/formalizacaoAvaliacao';

const t = ptBR.formalizacaoAvaliacao.formulario.verificacao;
const tp = ptBR.exameEletronico.conferenciaFacial.painel;

export interface DecisaoDoItemNaTela {
  decisao: DecisaoDoItem | null;
  observacao: string;
}

export type DecisoesDosItens = Readonly<Record<string, DecisaoDoItemNaTela>>;

const VAZIA: DecisaoDoItemNaTela = { decisao: null, observacao: '' };

function incompleto(d: DecisaoDoItemNaTela | undefined): boolean {
  return d?.decisao === 'retificado' && d.observacao.trim() === '';
}

/** Quantos itens retificados ainda não dizem o que se observa no lugar. */
export function itensIncompletos(
  parecer: ConferenciaFacialParecerData,
  decisoes: DecisoesDosItens,
): number {
  return itensDaVerificacao(parecer).filter((item) => incompleto(decisoes[item])).length;
}

/** Os itens decididos que vão ao servidor, ou `null` com algum retificado incompleto. */
export function verificacaoDe(
  parecer: ConferenciaFacialParecerData,
  decisoes: DecisoesDosItens,
): VerificacaoDoItemData[] | null {
  if (itensIncompletos(parecer, decisoes) > 0) return null;
  return itensDaVerificacao(parecer).flatMap((item) => {
    const d = decisoes[item] ?? VAZIA;
    if (d.decisao === null) return [];
    const retificado = d.decisao === 'retificado';
    return [
      {
        item,
        decisao: retificado ? 'retificado' : 'confirmado',
        observacao: retificado ? d.observacao.trim() : null,
      },
    ];
  });
}

function daQualidade(q: QualidadeDaImagemData): string {
  const limitantes = (['resolucao', 'nitidez', 'iluminacao', 'pose', 'oclusao'] as const)
    .filter((f) => q[f] !== 'adequado')
    .map((f) => t.fator[f]);
  const adequacao = t.adequacao[q.adequacao];
  return limitantes.length > 0 ? `${adequacao} (${limitantes.join(', ')})` : adequacao;
}

function rotulo(item: string): string {
  if (item === 'qualidade_documento_identidade' || item === 'qualidade_ccb') {
    return t.itemQualidade[item];
  }
  return tp.elementos[item as ElementoDoPainel];
}

function Registrado({
  parecer,
  item,
}: {
  parecer: ConferenciaFacialParecerData;
  item: string;
}): JSX.Element | null {
  const q = parecer.qualidade;
  if (item === 'qualidade_documento_identidade' && q) {
    return <p className="text-caption text-fg-muted">{daQualidade(q.documento_identidade)}</p>;
  }
  if (item === 'qualidade_ccb' && q) {
    return <p className="text-caption text-fg-muted">{daQualidade(q.ccb)}</p>;
  }
  const linha = parecer.painel.find((l) => l.elemento === item);
  if (!linha) return null;
  return (
    <p className="text-caption text-fg-muted">
      {t.documento}: {linha.identidade} · {t.formalizacao}: {linha.selfie} · {tp.graus[linha.grau]}
    </p>
  );
}

export interface VerificacaoPorItemProps {
  parecer: ConferenciaFacialParecerData;
  decisoes: DecisoesDosItens;
  tentou: boolean;
  onMudar: (decisoes: DecisoesDosItens) => void;
}

export function VerificacaoPorItem({
  parecer,
  decisoes,
  tentou,
  onMudar,
}: VerificacaoPorItemProps): JSX.Element {
  const faltam = itensIncompletos(parecer, decisoes);
  function mudar(item: string, d: Partial<DecisaoDoItemNaTela>): void {
    onMudar({ ...decisoes, [item]: { ...(decisoes[item] ?? VAZIA), ...d } });
  }
  return (
    <fieldset className="space-y-sc-2" data-testid="verificacao-por-item">
      <legend className="mb-[5px] text-caption text-fg-dim">{t.titulo}</legend>
      <p className="text-caption text-fg-muted">{t.dica}</p>
      {itensDaVerificacao(parecer).map((item) => {
        const d = decisoes[item] ?? VAZIA;
        const falta = tentou && incompleto(d);
        return (
          <div key={item} className="border-t border-border pt-sc-2" data-item={item}>
            <p className="text-body-sm text-fg">{rotulo(item)}</p>
            <Registrado parecer={parecer} item={item} />
            <div className="mt-1 flex flex-wrap gap-sc-3">
              {(['confirmado', 'retificado'] as const).map((decisao) => (
                <Radio
                  key={decisao}
                  name={`verificacao-${item}`}
                  value={decisao}
                  checked={d.decisao === decisao}
                  onChange={() => mudar(item, { decisao })}
                  data-decisao-do-item={decisao}
                >
                  {t[decisao]}
                </Radio>
              ))}
            </div>
            {d.decisao === 'retificado' ? (
              <Field label={t.observacao} error={falta ? t.faltaObservacao : undefined} required>
                <Textarea
                  value={d.observacao}
                  maxLength={500}
                  rows={2}
                  onChange={(e) => mudar(item, { observacao: e.target.value })}
                />
              </Field>
            ) : null}
          </div>
        );
      })}
      {tentou && faltam > 0 ? (
        <p className="text-caption text-danger-700" data-testid="verificacao-faltam">
          {t.faltam.replace('{n}', String(faltam))}
        </p>
      ) : null}
    </fieldset>
  );
}

export interface VerificacaoNoFormulario {
  /** O bloco da verificação, ou `null` fora da face ou sem o parecer em vigor. */
  campos: JSX.Element | null;
  /** `undefined` sem verificação a coletar; `null` com retificado incompleto; senão, os decididos. */
  coletada: () => VerificacaoDoItemData[] | null | undefined;
  limpar: () => void;
}

/** O estado da verificação para o formulário da revisão (só a face com o parecer lido). */
export function useVerificacao(
  parecer: ConferenciaFacialParecerData | null | undefined,
  tentou: boolean,
): VerificacaoNoFormulario {
  const [decisoes, setDecisoes] = useState<DecisoesDosItens>({});
  if (!parecer) return { campos: null, coletada: () => undefined, limpar: () => undefined };
  return {
    campos: (
      <VerificacaoPorItem
        parecer={parecer}
        decisoes={decisoes}
        tentou={tentou}
        onMudar={setDecisoes}
      />
    ),
    coletada: () => verificacaoDe(parecer, decisoes),
    limpar: () => setDecisoes({}),
  };
}
